import React, { useState } from 'react';
import { Loader2, PhoneCall, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useLang } from '@/lib/LangContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function ConsultationDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const c = t.consult || {};
  const [form, setForm] = useState({ name: '', phone: '', preferred_time: '', note: '', consent: true });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setSent(false); setError(null); setForm({ name: '', phone: '', preferred_time: '', note: '', consent: true }); }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const r = await fetch(`${BACKEND}/api/consultation-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
      setSent(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md" data-testid="consultation-dialog">
        {sent ? (
          <div className="py-6 text-center" data-testid="consultation-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#0f6e44]/10 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-[#0f6e44]" />
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-[#05223D]">{c.thanksTitle || 'Got it!'}</h3>
            <p className="mt-2 text-[#465B70]">{c.thanksBody || "We'll call you back during working hours."}</p>
            <Button onClick={close} className="btn-primary mt-5" data-testid="consultation-close">OK</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PhoneCall size={18} className="text-[#F25C05]" />
                {c.title || 'Book a consultation'}
              </DialogTitle>
              <DialogDescription>{c.subtitle}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} noValidate className="space-y-4" data-testid="consultation-form">
              <div className="space-y-1.5">
                <Label htmlFor="c-name">{c.name} *</Label>
                <Input id="c-name" required value={form.name} onChange={upd('name')} data-testid="consultation-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-phone">{c.phone} *</Label>
                <Input id="c-phone" required inputMode="tel" value={form.phone} onChange={upd('phone')} placeholder="+995 5XX XXX XXX" data-testid="consultation-phone" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-time">{c.time}</Label>
                <Input id="c-time" value={form.preferred_time} onChange={upd('preferred_time')} placeholder={c.timePlaceholder} data-testid="consultation-time" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-note">{c.note}</Label>
                <Textarea id="c-note" rows={2} value={form.note} onChange={upd('note')} data-testid="consultation-note" />
              </div>
              {error && <div className="rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={close} className="rounded-xl">{c.cancel || 'Cancel'}</Button>
                <Button type="submit" disabled={sending} className="btn-primary" data-testid="consultation-submit">
                  {sending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  {c.submit || 'Request a call'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
